import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className", "decorator", "disabled", "id", "labelText", "hideLabel", "onChange", "onClick", "onKeyDown", "invalid", "invalidText", "helperText", "light", "placeholder", "enableCounter", "maxCount", "counterMode", "warn", "warnText", "rows", "slug"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useContext, useRef, useState, useEffect, forwardRef } from 'react';
import cx from 'classnames';
import deprecate from '../../prop-types/deprecate.js';
import { WarningFilled, WarningAltFilled } from '@carbon/icons-react';
import { usePrefix } from '../../internal/usePrefix.js';
import '../FluidForm/FluidForm.js';
import { FormContext } from '../FluidForm/FormContext.js';
import { useAnnouncer } from '../../internal/useAnnouncer.js';
import useIsomorphicEffect from '../../internal/useIsomorphicEffect.js';
import { useMergedRefs } from '../../internal/useMergedRefs.js';
import { useId } from '../../internal/useId.js';
import { noopFn } from '../../internal/noopFn.js';
import '../Text/index.js';
import { Text } from '../Text/Text.js';
var frFn = forwardRef;
var TextArea = frFn(function (props, forwardRef) {
  var _cx, _cx2, _cx3, _cx4, _normalizedDecorator$;
  var className = props.className,
    decorator = props.decorator,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    id = props.id,
    labelText = props.labelText,
    hideLabel = props.hideLabel,
    _props$onChange = props.onChange,
    _onChange = _props$onChange === void 0 ? noopFn : _props$onChange,
    _props$onClick = props.onClick,
    _onClick = _props$onClick === void 0 ? noopFn : _props$onClick,
    _props$onKeyDown = props.onKeyDown,
    _onKeyDown = _props$onKeyDown === void 0 ? noopFn : _props$onKeyDown,
    _props$invalid = props.invalid,
    invalid = _props$invalid === void 0 ? false : _props$invalid,
    _props$invalidText = props.invalidText,
    invalidText = _props$invalidText === void 0 ? '' : _props$invalidText,
    _props$helperText = props.helperText,
    helperText = _props$helperText === void 0 ? '' : _props$helperText,
    light = props.light,
    _props$placeholder = props.placeholder,
    placeholder = _props$placeholder === void 0 ? '' : _props$placeholder,
    _props$enableCounter = props.enableCounter,
    enableCounter = _props$enableCounter === void 0 ? false : _props$enableCounter,
    _props$maxCount = props.maxCount,
    maxCount = _props$maxCount === void 0 ? undefined : _props$maxCount,
    _props$counterMode = props.counterMode,
    counterMode = _props$counterMode === void 0 ? 'character' : _props$counterMode,
    _props$warn = props.warn,
    warn = _props$warn === void 0 ? false : _props$warn,
    _props$warnText = props.warnText,
    warnText = _props$warnText === void 0 ? '' : _props$warnText,
    _props$rows = props.rows,
    rows = _props$rows === void 0 ? 4 : _props$rows,
    slug = props.slug,
    other = _objectWithoutProperties(props, _excluded);
  var prefix = usePrefix();
  var _useContext = useContext(FormContext),
    isFluid = _useContext.isFluid;
  var defaultValue = other.defaultValue,
    value = other.value;
  var textAreaInstanceId = useId();
  var textareaRef = useRef(null);
  var ref = useMergedRefs([forwardRef, textareaRef]);
  function getInitialTextCount() {
    var _textareaRef$current;
    var targetValue = defaultValue || value || ((_textareaRef$current = textareaRef.current) === null || _textareaRef$current === void 0 ? void 0 : _textareaRef$current.value) || '';
    var strValue = targetValue.toString();
    if (counterMode === 'character') {
      return strValue.length;
    } else {
      var _strValue$match;
      return ((_strValue$match = strValue.match(/\w+/g)) === null || _strValue$match === void 0 ? void 0 : _strValue$match.length) || 0;
    }
  }
  var _useState = useState(getInitialTextCount()),
    _useState2 = _slicedToArray(_useState, 2),
    textCount = _useState2[0],
    setTextCount = _useState2[1];
  useEffect(function () {
    setTextCount(getInitialTextCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultValue, counterMode]);
  useIsomorphicEffect(function () {
    if (other.cols && textareaRef.current) {
      textareaRef.current.style.width = '';
      textareaRef.current.style.resize = 'none';
    } else if (textareaRef.current) {
      textareaRef.current.style.width = "100%";
    }
  }, [other.cols]);
  var textareaProps = {
    id: id,
    onKeyDown: function onKeyDown(evt) {
      if (!disabled && enableCounter && counterMode === 'word') {
        var key = evt.which;
        if (maxCount && textCount >= maxCount && key === 32 || maxCount && textCount >= maxCount && key === 13) {
          evt.preventDefault();
        }
      }
      if (!disabled && _onKeyDown) {
        _onKeyDown(evt);
      }
    },
    onPaste: function onPaste(evt) {
      if (!disabled) {
        if (counterMode === 'word' && enableCounter && typeof maxCount !== 'undefined' && textareaRef.current !== null) {
          var existingWords = textareaRef.current.value.match(/\w+/g) || [];
          var pastedWords = evt.clipboardData.getData('Text').match(/\w+/g) || [];
          var totalWords = existingWords.length + pastedWords.length;
          if (totalWords > maxCount) {
            evt.preventDefault();
            var allowedWords = existingWords.concat(pastedWords).slice(0, maxCount);
            setTimeout(function () {
              setTextCount(maxCount);
            }, 0);
            textareaRef.current.value = allowedWords.join(' ');
          }
        }
      }
    },
    onChange: function onChange(evt) {
      if (!disabled) {
        if (counterMode == 'character') {
          var _evt$persist;
          evt === null || evt === void 0 || (_evt$persist = evt.persist) === null || _evt$persist === void 0 ? void 0 : _evt$persist.call(evt);
          // delay textCount assignation to give the textarea element value time to catch up if is a controlled input
          setTimeout(function () {
            var _evt$target;
            setTextCount((_evt$target = evt.target) === null || _evt$target === void 0 || (_evt$target = _evt$target.value) === null || _evt$target === void 0 ? void 0 : _evt$target.length);
          }, 0);
        } else if (counterMode == 'word') {
          if (!evt.target.value) {
            setTimeout(function () {
              setTextCount(0);
            }, 0);
            return;
          }
          if (enableCounter && typeof maxCount !== 'undefined' && textareaRef.current !== null) {
            var _evt$target2;
            var matchedWords = (_evt$target2 = evt.target) === null || _evt$target2 === void 0 || (_evt$target2 = _evt$target2.value) === null || _evt$target2 === void 0 ? void 0 : _evt$target2.match(/\w+/g);
            if (matchedWords && matchedWords.length <= maxCount) {
              textareaRef.current.removeAttribute('maxLength');
              setTimeout(function () {
                setTextCount(matchedWords.length);
              }, 0);
            } else if (matchedWords && matchedWords.length > maxCount) {
              setTimeout(function () {
                setTextCount(matchedWords.length);
              }, 0);
            }
          }
        }
        if (_onChange) {
          _onChange(evt);
        }
      }
    },
    onClick: function onClick(evt) {
      if (!disabled && _onClick) {
        _onClick(evt);
      }
    }
  };
  var formItemClasses = cx("".concat(prefix, "--form-item"), className);
  var textAreaWrapperClasses = cx("".concat(prefix, "--text-area__wrapper"), (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--text-area__wrapper--cols"), other.cols), _defineProperty(_cx, "".concat(prefix, "--text-area__wrapper--readonly"), other.readOnly), _defineProperty(_cx, "".concat(prefix, "--text-area__wrapper--warn"), warn), _defineProperty(_cx, "".concat(prefix, "--text-area__wrapper--slug"), slug), _defineProperty(_cx, "".concat(prefix, "--text-area__wrapper--decorator"), decorator), _cx));
  var labelClasses = cx("".concat(prefix, "--label"), (_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "--visually-hidden"), hideLabel && !isFluid), _defineProperty(_cx2, "".concat(prefix, "--label--disabled"), disabled), _cx2));
  var textareaClasses = cx("".concat(prefix, "--text-area"), (_cx3 = {}, _defineProperty(_cx3, "".concat(prefix, "--text-area--light"), light), _defineProperty(_cx3, "".concat(prefix, "--text-area--invalid"), invalid), _defineProperty(_cx3, "".concat(prefix, "--text-area--warn"), warn), _cx3));
  var counterClasses = cx("".concat(prefix, "--label"), (_cx4 = {}, _defineProperty(_cx4, "".concat(prefix, "--label--disabled"), disabled), _defineProperty(_cx4, "".concat(prefix, "--text-area__label-counter"), true), _cx4));
  var helperTextClasses = cx("".concat(prefix, "--form__helper-text"), _defineProperty({}, "".concat(prefix, "--form__helper-text--disabled"), disabled));
  var label = labelText ? /*#__PURE__*/React.createElement(Text, {
    as: "label",
    htmlFor: id,
    className: labelClasses
  }, labelText) : null;
  var counter = enableCounter && maxCount && (counterMode === 'character' || counterMode === 'word') ? /*#__PURE__*/React.createElement(Text, {
    as: "div",
    className: counterClasses
  }, "".concat(textCount, "/").concat(maxCount)) : null;
  var helperId = !helperText ? undefined : "text-area-helper-text-".concat(textAreaInstanceId);
  var helper = helperText ? /*#__PURE__*/React.createElement(Text, {
    as: "div",
    id: helperId,
    className: helperTextClasses
  }, helperText) : null;
  var errorId = id + '-error-msg';
  var error = invalid ? /*#__PURE__*/React.createElement(Text, {
    as: "div",
    role: "alert",
    className: "".concat(prefix, "--form-requirement"),
    id: errorId
  }, invalidText, isFluid && /*#__PURE__*/React.createElement(WarningFilled, {
    className: "".concat(prefix, "--text-area__invalid-icon")
  })) : null;
  var warning = warn ? /*#__PURE__*/React.createElement(Text, {
    as: "div",
    role: "alert",
    className: "".concat(prefix, "--form-requirement")
  }, warnText, isFluid && /*#__PURE__*/React.createElement(WarningAltFilled, {
    className: "".concat(prefix, "--text-area__invalid-icon ").concat(prefix, "--text-area__invalid-icon--warning")
  })) : null;
  var ariaDescribedBy;
  if (invalid) {
    ariaDescribedBy = errorId;
  } else if (!invalid && !warn && !isFluid && helperText) {
    ariaDescribedBy = helperId;
  }
  if (enableCounter) {
    // handle different counter mode
    if (counterMode == 'character') {
      textareaProps.maxLength = maxCount;
    }
  }
  var announcerRef = useRef(null);
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    prevAnnouncement = _useState4[0],
    setPrevAnnouncement = _useState4[1];
  var ariaAnnouncement = useAnnouncer(textCount, maxCount, counterMode === 'word' ? 'words' : undefined);
  useEffect(function () {
    if (ariaAnnouncement && ariaAnnouncement !== prevAnnouncement) {
      var announcer = announcerRef.current;
      if (announcer) {
        // Clear the content first
        announcer.textContent = '';

        // Set the new content after a small delay
        var timeoutId = setTimeout(function () {
          if (announcer) {
            announcer.textContent = ariaAnnouncement;
            setPrevAnnouncement(ariaAnnouncement);
          }
        }, counterMode === 'word' ? 2000 : 1000);

        //clear the timeout
        return function () {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        };
      }
    }
  }, [ariaAnnouncement, prevAnnouncement, counterMode]);
  var input = /*#__PURE__*/React.createElement("textarea", _extends({}, other, textareaProps, {
    placeholder: placeholder,
    "aria-readonly": other.readOnly ? true : false,
    className: textareaClasses,
    "aria-invalid": invalid,
    "aria-describedby": ariaDescribedBy,
    disabled: disabled,
    rows: rows,
    readOnly: other.readOnly,
    ref: ref
  }));

  // AILabel is always size `mini`
  var normalizedDecorator = /*#__PURE__*/React.isValidElement(slug !== null && slug !== void 0 ? slug : decorator) ? slug !== null && slug !== void 0 ? slug : decorator : null;
  if (normalizedDecorator && ((_normalizedDecorator$ = normalizedDecorator['type']) === null || _normalizedDecorator$ === void 0 ? void 0 : _normalizedDecorator$.displayName) === 'AILabel') {
    normalizedDecorator = /*#__PURE__*/React.cloneElement(normalizedDecorator, {
      size: 'mini'
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: formItemClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--text-area__label-wrapper")
  }, label, counter), /*#__PURE__*/React.createElement("div", {
    className: textAreaWrapperClasses,
    "data-invalid": invalid || null
  }, invalid && !isFluid && /*#__PURE__*/React.createElement(WarningFilled, {
    className: "".concat(prefix, "--text-area__invalid-icon")
  }), warn && !invalid && !isFluid && /*#__PURE__*/React.createElement(WarningAltFilled, {
    className: "".concat(prefix, "--text-area__invalid-icon ").concat(prefix, "--text-area__invalid-icon--warning")
  }), input, slug ? normalizedDecorator : decorator ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--text-area__inner-wrapper--decorator")
  }, normalizedDecorator) : '', /*#__PURE__*/React.createElement("span", {
    className: "".concat(prefix, "--text-area__counter-alert"),
    role: "alert",
    "aria-live": "assertive",
    "aria-atomic": "true",
    ref: announcerRef
  }, ariaAnnouncement), isFluid && /*#__PURE__*/React.createElement("hr", {
    className: "".concat(prefix, "--text-area__divider")
  }), isFluid && invalid ? error : null, isFluid && warn && !invalid ? warning : null), !invalid && !warn && !isFluid ? helper : null, invalid && !isFluid ? error : null, warn && !invalid && !isFluid ? warning : null);
});
TextArea.displayName = 'TextArea';
export { TextArea as default };